import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, signInWithCustomToken } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { ControlLabel, Button } from 'react-bootstrap';
import FormHeader from './FormHeader';
import { setAuthenticatedUser } from '../../../shared/actions';
import withDefaults from './withDefaults'
import { defaultMessages } from '../../../libs/i18n/default';
import { redirectToLogin, checkForExistingHmsUser, postCreateAuthenticatedUser, checkForExistingFirebaseUser } from '../../../shared/lou/auth'

const FirebaseCreateAccountForm = ({ page, content, intl, intl: { formatMessage }, setEmailVerified }) => {
  const dispatch = useDispatch()
  const territory = useSelector(state => state.territory)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailValid, setEmailValid] = useState(null)
  const [passwordValid, setPasswordValid] = useState(null)
  const [checkedSignInMethods, setCheckedSignInMethods] = useState(false)
  const [userExists, setUserExists] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const emailInputRef = useRef(null);
  const timer = useRef()

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const [checkingToken, setCheckingToken] = useState(token ? true : false)

  const createAuthenticatedUser = async (e) => {
    const response = await postCreateAuthenticatedUser(email, password, 'guest')
    const data = await response.json()

    if (data.success) {
      dispatch(setAuthenticatedUser(data.authenticated_user));
      setEmailVerified(false)
    } else {
      setSubmitting(false)
      console.log(data.message)
    }
  }

  const next = async (e) => {
    e.preventDefault()

    setSubmitting(true)

    if (!checkedSignInMethods) {
      const hmsUserExists = await checkForExistingHmsUser(territory, email)
      const firebaseUserExists = await checkForExistingFirebaseUser(territory, email);

      if (hmsUserExists || firebaseUserExists) {
        setUserExists(true)
        redirectToLogin(territory, email)
      } else {
        setCheckedSignInMethods(true)
        setSubmitting(false)
      }
    } else if (checkedSignInMethods) {
      await createAuthenticatedUser(e)
    }
  }

  const submitText = () => {
    if (!checkedSignInMethods) { return defaultMessages.clientCommonButtonsContinue }

    return defaultMessages.clientCommonButtonsSignup
  }

  const buttonDisabled = () => {
    if (!checkedSignInMethods) return !emailValid

    return !(emailValid && passwordValid && !userExists)
  }

  useEffect(() => {
    if(!email) return

    if (timer.current != null) {
      clearTimeout(timer.current)
    }

    timer.current = setTimeout(() => {
      const isValid = emailInputRef.current.checkValidity();
      setEmailValid(isValid)

    }, 500)
  }, [email])

  useEffect(() => {
    if(!password) return

    if (timer.current != null) {
      clearTimeout(timer.current)
    }

    timer.current = setTimeout(() => {
      fetch(`/users/validate_password`, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        },
        body: JSON.stringify({ password })
      }).then(response => response.json())
        .then(data => {
          setPasswordValid(data.valid)
        }
      );
    }, 500)
  }, [password])

  return (
    <div className="register_form" >
      <form onSubmit={(e) => next(e)}>
        <FormHeader title={content.pages.register.title} />
        <div className="text-center mt-3 smaller">{formatMessage(defaultMessages.clientCommonSignupPrompt)}</div>
        <ControlLabel>{formatMessage(defaultMessages.clientCommonEmail)}</ControlLabel>
        <input
          className="form-control"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          ref={emailInputRef}
          style={{marginBottom: '5px'}}
          required
          pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
        />
        {emailValid === false && (
          <div>
            <p style={{ color: 'red', fontSize: 'smaller' }}>
              {formatMessage(defaultMessages.clientGuestPagesRegisterPopoversEmailInvalid)}
            </p>
          </div>
        )}
        {checkedSignInMethods && (
          <>
            <ControlLabel>{formatMessage(defaultMessages.clientGuestPagesRegisterLabelsPassword)}</ControlLabel>
            <input
              className="form-control"
              type="password"
              value={password}
              style={{marginBottom: '5px'}}
              placeholder={formatMessage(defaultMessages.clientGuestPagesRegisterPlaceholdersPassword)}
              onChange={e => setPassword(e.target.value)}
              required
            />
            {!passwordValid && (
              <div>
                <p style={{ color: passwordValid === false ? 'red' : 'black', fontSize: 'smaller' }}>
                  {formatMessage(defaultMessages.clientCommonPasswordRequirements)}
                </p>
              </div>
            )}
          </>
        )}
        <div className="text-center">
          <Button
            type="submit"
            id="firebase-register"
            disabled={buttonDisabled()}
            className="btn btn-primary btn-lg"
            style={{marginTop: '10px'}}
          >
            {submitting ? <i className="fa fa-fw fa-spinner fa-spin" /> : formatMessage(submitText())}
          </Button>
          {userExists && (
            <div style={{ marginTop: 10 }}>
              <p>
                {formatMessage(defaultMessages.clientFirebaseEmailInUse)}
              </p>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

FirebaseCreateAccountForm.propTypes = {
   page: PropTypes.string,
   intl: PropTypes.object,
   content: PropTypes.object
};

export default withDefaults(FirebaseCreateAccountForm);
